import type { CardPack } from "./card";
import type { MsgKey } from "./i18n";

/**
 * 「物品掉落配置」页的纯逻辑：把各物品包的 drops 按玩家等级转置成一行一级。
 * 口径与服务端写入口 putCardPackDrops、发布闸 CARD_LEVEL_TOTAL、运行时 levelPackCandidates 一致：
 * 只有启用且有卡的物品包计入合计；停用 / 无卡包的旧条目只展示、不计入、保存时不动。
 */

export const LEVEL_PAGE_SIZE = 20;
export const TOTAL_BPS = 10000;

/** 可配置的玩家等级范围：经典体系 0 ~ 全部关卡建筑数（0 = 刚注册），萌宠小镇 1 ~ 最大星 / 每级星数 + 1 */
export function playerLevelRange(buildingSystem: string | undefined, maxStar: number, starsPerLevel: number, levels: ReadonlyArray<{ N: number }>) {
  return buildingSystem === "pettown"
    ? { min: 1, max: Math.floor(maxStar / Math.max(1, starsPerLevel)) + 1 }
    : { min: 0, max: levels.reduce((sum, l) => sum + l.N, 0) };
}

export const dropEligible = (p: CardPack) => !p.disabled && p.cards.length > 0;
/** UI 态百分数字符串 → bps（与 toServerPack 同一换算） */
export const toBps = (rate: string) => Math.round(Number(rate) * 100);
export const fromBps = (bps: number) => String(bps / 100);

export interface LevelEntry { packId: string; rateBps: number }
export interface LevelRow {
  level: number;
  /** 计入合计的条目（启用且有卡） */
  entries: LevelEntry[];
  /** 不计入的旧条目：保存这一级时服务端原样保留 */
  ignored: Array<LevelEntry & { why: "off" | "empty" }>;
  total: number;
}

export function levelRow(packs: readonly CardPack[], level: number): LevelRow {
  const entries: LevelEntry[] = [];
  const ignored: LevelRow["ignored"] = [];
  for (const p of packs) {
    const d = (p.drops ?? []).find((x) => x.level === level);
    const rateBps = d ? toBps(d.rate) : 0;
    if (!(rateBps > 0)) continue;
    if (dropEligible(p)) entries.push({ packId: p.id, rateBps });
    else ignored.push({ packId: p.id, rateBps, why: p.disabled ? "off" : "empty" });
  }
  return { level, entries, ignored, total: entries.reduce((s, e) => s + e.rateBps, 0) };
}

/** 合计异常 = 配了但不是 100%（空 = 不掉落，合法） */
export const badTotal = (total: number) => total !== 0 && total !== TOTAL_BPS;

export interface DraftEntry { key: string; packId: string; rate: string }

/** 编辑中的一行能不能存：第一条问题（与服务端 checkDropLevels 同一套规则），没有问题返回 null */
export function draftIssue(entries: readonly DraftEntry[]): { key: MsgKey; vars?: Record<string, string | number> } | null {
  if (entries.some((e) => !e.packId)) return { key: "drop.err.pack" };
  if (entries.some((e) => { const b = toBps(e.rate); return e.rate.trim() === "" || !Number.isFinite(b) || b <= 0 || b > TOTAL_BPS; })) return { key: "drop.err.rate" };
  if (new Set(entries.map((e) => e.packId)).size !== entries.length) return { key: "drop.err.dup" };
  const total = draftTotal(entries);
  if (entries.length && total !== TOTAL_BPS) return { key: "drop.err.total", vars: { total: total / 100 } };
  return null;
}

export const draftTotal = (entries: readonly DraftEntry[]) =>
  entries.reduce((s, e) => s + (Number.isFinite(toBps(e.rate)) ? toBps(e.rate) : 0), 0);
